import { FC, useEffect, useRef, useState } from 'react';
import {
  CustomDropdownArrow,
  CustomDropdownButton,
  CustomDropdownContainer,
  CustomDropdownContent,
  CustomDropdownList,
  CustomDropdownListDown,
  CustomDropdownOption,
  CustomDropdownOptionCheck
} from '../styles';

export interface Option {
  value: string;
  label: string;
  icon?: string;
}

interface CustomMultiSelectDropdownProps {
  value: string[];
  onChange: (values: string[]) => void;
  options: Option[];
  placeholder?: string;
  disabled?: boolean;
  width?: string;
  direction?: 'up' | 'down';
  id?: string;
  // The trigger is a div[role="button"], which label/htmlFor cannot name;
  // aria-labelledby is the only association that works on it.
  ariaLabelledBy?: string;
}

const CustomMultiSelectDropdown: FC<CustomMultiSelectDropdownProps> = ({
  value = [],
  onChange,
  options,
  placeholder = 'Select...',
  disabled = false,
  width,
  direction = 'up',
  id,
  ariaLabelledBy
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideInteraction = (event: Event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleWindowBlur = () => {
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideInteraction, true);
      document.addEventListener('click', handleOutsideInteraction, true);
      document.addEventListener('touchstart', handleOutsideInteraction, true);
      window.addEventListener('blur', handleWindowBlur);
      window.addEventListener('focus', handleOutsideInteraction, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideInteraction, true);
      document.removeEventListener('click', handleOutsideInteraction, true);
      document.removeEventListener('touchstart', handleOutsideInteraction, true);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleOutsideInteraction, true);
    };
  }, [isOpen]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsOpen(!isOpen);
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handleOptionKeyDown = (event: React.KeyboardEvent, optionValue: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleOption(optionValue);
    }
  };

  const toggleOption = (optionValue: string) => {
    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };

  const selectedLabels = options.filter((opt) => value.includes(opt.value)).map((opt) => opt.label);
  const singleLabel = value.length === 1 ? selectedLabels[0] : undefined;

  const getDisplayContent = () => {
    if (value.length === 0) {
      return <span>{placeholder}</span>;
    }

    if (value.length === 1) {
      return (
        <CustomDropdownContent>
          <span>{singleLabel}</span>
        </CustomDropdownContent>
      );
    }

    return (
      <CustomDropdownContent>
        <span>{value.length} reasons selected</span>
      </CustomDropdownContent>
    );
  };

  const DropdownListComponent = direction === 'down' ? CustomDropdownListDown : CustomDropdownList;

  return (
    <CustomDropdownContainer ref={dropdownRef} style={width ? { width } : {}}>
      <CustomDropdownButton
        id={id}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-labelledby={ariaLabelledBy}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        title={selectedLabels.join(', ') || undefined}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        isOpen={isOpen}
        style={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1
        }}
      >
        {getDisplayContent()}
        {/* Decorative: role="button" plus aria-expanded already convey this, and a
            non-empty alt lands in the trigger's accessible name as "dropdown". */}
        <CustomDropdownArrow src="/assets/images/dropdown.svg" alt="" isOpen={isOpen} />
      </CustomDropdownButton>

      {isOpen && !disabled && (
        <DropdownListComponent role="listbox">
          {options.map((option) => (
            <CustomDropdownOption
              key={option.value}
              role="option"
              tabIndex={0}
              aria-selected={value.includes(option.value)}
              onClick={() => toggleOption(option.value)}
              onKeyDown={(e) => handleOptionKeyDown(e, option.value)}
              selected={value.includes(option.value)}
            >
              <span>{option.label}</span>
              {value.includes(option.value) ? (
                <CustomDropdownOptionCheck
                  src="/assets/images/dropdown-selected.svg"
                  alt="selected"
                />
              ) : null}
            </CustomDropdownOption>
          ))}
        </DropdownListComponent>
      )}
    </CustomDropdownContainer>
  );
};

export default CustomMultiSelectDropdown;
